// HospitalRating.tsx
import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { Button } from "@progress/kendo-react-buttons";
import { Typography } from "@progress/kendo-react-common";
import {
  Field,
  Form,
  FormElement,
  FormRenderProps,
} from "@progress/kendo-react-form";
import { GridLayout, GridLayoutItem } from "@progress/kendo-react-layout";
import {
  Grid as KendoGrid,
  GridColumn as Column,
  GridPageChangeEvent,
  GridCustomHeaderCellProps,
  GridCustomCellProps,
} from "@progress/kendo-react-grid";
import { PagerTargetEvent } from "@progress/kendo-react-data-tools";
import { HeaderThElement } from "@progress/kendo-react-data-tools";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { closeDialog, openDialog } from "../../components/dialog/dialogSlice";
import AppDialog, { DialogCloseButton } from "../../components/dialog/Dialog";
import IconButton from "../../components/common/IconButton";
import ButtonWithLoading from "../../components/common/ButtonWithLoading";
import ShadowCard from "../../components/common/ShadowCard";
import AlertBox from "../../components/common/AlertBox";
import { requiredValidator } from "../../components/formFields/CommonValidator";
import FormTextField from "../../components/formFields/FormTextField";
import FormTextArea from "../../components/formFields/FormTextArea";
import {
  deleteHospitalRating,
  getHospitalRatingsByID,
  insertHospitalRating,
  updateHospitalRating,
} from "./services/hospital.service";
import { clearHospitalRatings, clearHospitalDetails } from "./hospitalSlice";
import { MdDelete, MdEdit, MdStar, MdStarBorder } from "react-icons/md";
import IconComponent from "../../components/common/IconComponent";
import FormRatingField from "../../components/formFields/FormRatingField";
import ExpandableText from "../../components/common/ExpandableText";

interface PageState {
  skip: number;
  take: number;
}

const initialDataState: PageState = { skip: 0, take: 10 };

const HospitalRatingsArray: React.FC = () => {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const HospitalID = location.state?.HospitalID;
  const [editRating, setEditRating] = useState<any>(null);
  const [deleteRatingId, setDeleteRatingId] = useState<number | null>(null);
  const [formKey, setFormKey] = useState(1);
  const [page, setPage] = useState<PageState>(initialDataState);
  const [pageSizeValue, setPageSizeValue] = useState<
    number | string | undefined
  >();

  const dialogName = useAppSelector((state) => state.dialog.dialogName);
  const ratings = useAppSelector((state) => state.hospital.HospitalRatings);
  const loading = useAppSelector((state) => state.hospital.loading);
  const formLoading = useAppSelector((state) => state.hospital.formLoading);

  useEffect(() => {
    setFormKey(formKey + 1);
  }, [ratings, editRating]);

  useEffect(() => {
    if (HospitalID) {
      dispatch(getHospitalRatingsByID(HospitalID));
    }
    return () => {
      dispatch(clearHospitalRatings());
      dispatch(clearHospitalDetails());
    };
  }, [HospitalID]);

  const pageChange = (event: GridPageChangeEvent) => {
    const targetEvent = event.targetEvent as PagerTargetEvent;
    const take = targetEvent.value === "All" ? ratings.length : event.page.take;

    if (targetEvent.value) {
      setPageSizeValue(targetEvent.value);
    }
    setPage({
      ...event.page,
      take,
    });
  };

  const handleAddRating = async (values: any) => {
    const ratingData = {
      HospitalID: HospitalID,
      Rating: parseInt(values.Rating),
      Title: values.Title,
      Reviews: values.Reviews,
    };
    const response = await dispatch(insertHospitalRating(ratingData));
    if (response?.meta?.requestStatus === "fulfilled") {
      dispatch(getHospitalRatingsByID(HospitalID));
      setEditRating(null);
      const formElement = document.querySelector("form");
      if (formElement) {
        formElement.reset();
      }
    }
  };

  const handleUpdateRating = async (values: any) => {
    const ratingData = {
      ID: editRating.ID,
      HospitalID: HospitalID,
      Rating: parseInt(values.Rating),
      Title: values.Title,
      Reviews: values.Reviews,
    };
    const response = await dispatch(updateHospitalRating(ratingData));
    if (response?.meta?.requestStatus === "fulfilled") {
      dispatch(getHospitalRatingsByID(HospitalID));
      setEditRating(null);
    }
  };

  const handleDeleteRating = async () => {
    if (deleteRatingId) {
      const response = await dispatch(deleteHospitalRating(deleteRatingId));
      if (response?.meta?.requestStatus === "fulfilled") {
        dispatch(getHospitalRatingsByID(HospitalID));
        dispatch(closeDialog());
        setDeleteRatingId(null);
      }
    }
  };

  const renderStars = (rating: number) => {
    const fullStars = Math.floor(rating);
    const emptyStars = 5 - fullStars;
    return (
      <div style={{ display: "flex", alignItems: "center", gap: "2px" }}>
        {[...Array(fullStars)].map((_, i) => (
          <IconComponent
            icon={MdStar}
            key={`full-${i}`}
            style={{ color: "#ffc107", fontSize: "16px" }}
          />
        ))}
        {[...Array(emptyStars)].map((_, i) => (
          <IconComponent
            icon={MdStarBorder}
            key={`empty-${i}`}
            style={{ color: "#ffc107", fontSize: "16px" }}
          />
        ))}
      </div>
    );
  };

  // Grid Action Cell Component
  const MyEditCommandCell = (props: any) => {
    const handleEdit = (item: any) => {
      setEditRating({ ...item });
    };

    const handleOpenDeleteDialog = (ID: number) => {
      dispatch(openDialog("deleteHospitalRating"));
      setDeleteRatingId(ID);
    };

    return (
      <td
        className={props.className}
        style={{
          ...props.style,
          right: 0,
          textAlign: "center",
          display: "flex",
          justifyContent: "space-evenly",
        }}
      >
        <IconButton
          type="button"
          fillMode="flat"
          size="small"
          title="Edit"
          onClick={() => handleEdit(props.dataItem)}
          icon={MdEdit}
          iconClassName="absolute-position"
          iconStyle={{ fontSize: "20px" }}
        />
        <IconButton
          type="button"
          fillMode="flat"
          size="small"
          title="Delete"
          className="color-red"
          onClick={() => handleOpenDeleteDialog(props.dataItem.ID)}
          icon={MdDelete}
          iconClassName="absolute-position"
          iconStyle={{ fontSize: "24px" }}
        />
      </td>
    );
  };

  // Rating Cell Component
  const RatingCell = (props: any) => {
    return (
      <td style={{ ...props.style }} className={props.className}>
        <div className="d-flex">
          {renderStars(props.dataItem?.Rating)}
          <span style={{ marginLeft: "8px", fontSize: "12px", color: "#666" }}>
            ({props.dataItem?.Rating}/5)
          </span>
        </div>
      </td>
    );
  };

  // Header Custom Cell
  const HeaderCustomCell = (props: GridCustomHeaderCellProps) => (
    <HeaderThElement
      columnId={props.thProps?.columnId || ""}
      {...props.thProps}
      className={`table-header ${props.index === 4 ? "locked-header" : ""}`}
    >
      {props.children}
    </HeaderThElement>
  );

  const CustomCell = (props: any) => {
    return (
      <td {...props.tdProps} colSpan={1} className={"table-cell"}>
        {props.children}
      </td>
    );
  };

  const MyDataCustomCell = (props: GridCustomCellProps) => (
    <CustomCell {...props} />
  );

  return (
    <>
      {dialogName === "deleteHospitalRating" && (
        <AppDialog>
          <>
            <Typography.h5>Delete Rating & Review</Typography.h5>
            <GridLayout>
              <GridLayoutItem>
                <Typography.p>
                  Are you sure you want to delete this rating and review?
                </Typography.p>
              </GridLayoutItem>
              <GridLayoutItem
                style={{
                  display: "flex",
                  justifyContent: "end",
                  gap: "10px",
                }}
              >
                <DialogCloseButton themeColor="error" />
                <Button
                  fillMode={"solid"}
                  themeColor={"error"}
                  onClick={handleDeleteRating}
                  type="button"
                  disabled={loading}
                >
                  Delete
                </Button>
              </GridLayoutItem>
            </GridLayout>
          </>
        </AppDialog>
      )}

      {/* Header */}
      <GridLayout style={{ marginTop: 20 }} gap={{ rows: 10, cols: 10 }}>
        {/* Add/Edit Rating Form */}
        <GridLayoutItem colSpan={4}>
          <ShadowCard style={{ padding: 20 }}>
            <Typography.h4 style={{ margin: "0 0 20px 0", color: "#495057" }}>
              {editRating ? "Edit Rating & Review" : "Add New Rating & Review"}
            </Typography.h4>

            <Form
              key={formKey}
              onSubmit={editRating ? handleUpdateRating : handleAddRating}
              initialValues={
                editRating || {
                  Rating: "",
                  Title: "",
                  Reviews: "",
                }
              }
              render={(formRenderProps: FormRenderProps) => (
                <FormElement>
                  <GridLayout gap={{ rows: 15, cols: 20 }}>
                    <GridLayoutItem colSpan={2} rowSpan={2}>
                      <Field
                        name="Rating"
                        label="Rating (1-5)"
                        placeholder="Enter rating from 1 to 5"
                        component={FormRatingField}
                        type="number"
                        validator={requiredValidator}
                        astrike={true}
                      />
                      <Field
                        name="Title"
                        label="Review Title"
                        placeholder="E.g., Excellent Service, Good Experience"
                        component={FormTextField}
                        validator={requiredValidator}
                        astrike={true}
                      />
                    </GridLayoutItem>

                    <GridLayoutItem colSpan={2}>
                      <Field
                        name="Reviews"
                        label="Review Description"
                        placeholder="Write your detailed review about the hospital..."
                        component={FormTextArea}
                        rows={4}
                        validator={requiredValidator}
                        astrike={true}
                      />
                    </GridLayoutItem>

                    <GridLayoutItem
                      colSpan={4}
                      style={{
                        display: "flex",
                        justifyContent: "end",
                        gap: "10px",
                      }}
                    >
                      {editRating && (
                        <Button
                          type="button"
                          fillMode={"outline"}
                          themeColor={"secondary"}
                          onClick={() => setEditRating(null)}
                        >
                          Cancel Edit
                        </Button>
                      )}
                      <ButtonWithLoading
                        label={editRating ? "Update Review" : "Add Review"}
                        type="submit"
                        disabled={!formRenderProps.allowSubmit || formLoading}
                        loading={formLoading}
                      />
                    </GridLayoutItem>
                  </GridLayout>
                </FormElement>
              )}
            />
          </ShadowCard>
        </GridLayoutItem>

        {/* Ratings Grid Table */}
        <GridLayoutItem colSpan={4}>
          <ShadowCard style={{ padding: 20 }}>
            <Typography.h4 style={{ margin: "0 0 20px 0", color: "#495057" }}>
              All Reviews ({ratings?.length || 0})
            </Typography.h4>

            {ratings && ratings.length > 0 ? (
              <KendoGrid
                style={{ marginTop: 20, maxHeight: "calc(100vh - 400px)" }}
                data={ratings.slice(page.skip, page.take + page.skip)}
                skip={page.skip}
                take={page.take}
                total={ratings.length}
                pageable={{
                  buttonCount: 5,
                  pageSizes: [5, 10, 15, 20, 25, "All"],
                  pageSizeValue: pageSizeValue,
                  type: "input",
                }}
                onPageChange={pageChange}
                cells={{
                  headerCell: HeaderCustomCell,
                  data: MyDataCustomCell,
                }}
              >
                <Column
                  field="Title"
                  title="Review Title"
                  width={200}
                  filterable={true}
                />
                <Column
                  field="Rating"
                  title="Rating"
                  width={150}
                  cells={{ data: RatingCell }}
                  filterable={false}
                />
                <Column
                  field="Reviews"
                  title="Review Description"
                  cells={{
                    data: (props: any) => (
                      <td
                        style={{ ...props.style }}
                        className={props.className}
                      >
                        <ExpandableText text={props.dataItem?.Reviews} />
                      </td>
                    ),
                  }}
                />
                <Column
                  field="ID"
                  title="Actions"
                  width={110}
                  cells={{ data: MyEditCommandCell }}
                  filterable={false}
                  locked={true}
                />
              </KendoGrid>
            ) : (
              <AlertBox />
            )}
          </ShadowCard>
        </GridLayoutItem>
      </GridLayout>
    </>
  );
};

export default HospitalRatingsArray;
